import { api } from './client'
import type { DataMode } from '../dataMode'
import type { AvatarPresetId } from '../avatarPresets'
import type { QuestId } from '../types'

export interface Me {
  userId: string
  displayName: string
  favouriteTeam: string | null
  /** Which world the server is running in. Absent on an older server. */
  dataMode?: DataMode
  /**
   * Whether the server considers this account an admin.
   *
   * Optional for the same reason `dataMode` is — an older server does not send it, and the caller
   * falls back to the client-side check. The client must not compute this itself: `ADMIN_EMAILS`
   * lives in the environment, and the browser bundle has no environment.
   */
  isAdmin?: boolean
  /**
   * Which quests this deployment runs — the enabled set, in catalog order, from
   * the server's `QUESTS_ENABLED`. Empty means no quest section renders anywhere.
   *
   * Optional for the same reason the two fields above are: an older server does not send it. The
   * client cannot derive it — the flag is an environment variable, and the bundle has no
   * environment — so absent is treated as "no quests" rather than guessed at.
   */
  questsEnabled?: QuestId[]
  /**
   * The first gameweek this account may play, when that isn't the season's first. An account
   * created after a gameweek's deadline is never auto-filled or scored for it, so the Predict tab
   * shows that week's fixtures with no picks and says where their season starts. Null (or absent,
   * on an older server) means they were here from the start and nothing is shown.
   */
  firstPlayableGameweek?: number | null
  avatarPreset: AvatarPresetId | null
  email: string
}

export function loadMe(): Promise<Me> {
  return api.get('/me')
}

export function updateDisplayName(displayName: string): Promise<{ displayName: string }> {
  return api.put('/me/display-name', { displayName })
}

export function updateFavouriteTeam(favouriteTeam: string | null): Promise<{ favouriteTeam: string | null }> {
  return api.put('/me/favourite-team', { favouriteTeam })
}

/**
 * Delete my account and everything in it.
 *
 * `confirm` is the display name, typed by the user — the server checks it again, so this is not
 * the protection, only the request. Takes no id: the route acts on the session's own account and
 * cannot be pointed anywhere else.
 */
export function deleteMyAccount(confirm: string): Promise<{ deleted: true }> {
  return api.request<{ deleted: true }>('/me', {
    method: 'DELETE',
    body: JSON.stringify({ confirm }),
  })
}

export function updateAvatarPreset(
  avatarPreset: AvatarPresetId | null
): Promise<{ avatarPreset: AvatarPresetId | null }> {
  return api.put('/me/avatar', { avatarPreset })
}
